//---------------------------------------------------------------------
//
// QR Code Generator for JavaScript
//
// Copyright (c) 2009 Kazuhiko Arase
//
// URL: http://www.d-project.com/
//
// Licensed under the MIT license:
//  http://www.opensource.org/licenses/mit-license.php
//
// The word 'QR Code' is registered trademark of
// DENSO WAVE INCORPORATED
//  http://www.denso-wave.com/qrcode/faqpatent-e.html
//
//---------------------------------------------------------------------

/*
 * Vendored for Almikia (Epic EQ2, TASK-101) from the qrcode-generator
 * (Kazuhiko Arase, MIT) copy embedded in the owner's quoter reference app.
 * No npm dependency was added (installs are denied). Differences from the
 * original, none of which change the produced matrix:
 *  - ported to strict TypeScript (no globals, ES module export);
 *  - only the Byte mode with UTF-8 input is kept (the Numeric, Alphanumeric
 *    and Kanji modes, the GIF/table/ASCII output helpers and the Shift-JIS
 *    tables were dropped because nothing here uses them);
 *  - the render side lives in React (`src/components/atomic-crm/quoter/
 *    QrCode.tsx`), which draws the modules as an SVG path.
 */

export type QrErrorCorrectionLevel = "L" | "M" | "Q" | "H";

const MODE_8BIT_BYTE = 1 << 2;

const ERROR_CORRECTION_LEVEL: Record<QrErrorCorrectionLevel, number> = {
  L: 1,
  M: 0,
  Q: 3,
  H: 2,
};

const PATTERN_POSITION_TABLE: number[][] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
  [6, 28, 50, 72, 94],
  [6, 26, 50, 74, 98],
  [6, 30, 54, 78, 102],
  [6, 28, 54, 80, 106],
  [6, 32, 58, 84, 110],
  [6, 30, 58, 86, 114],
  [6, 34, 62, 90, 118],
  [6, 26, 50, 74, 98, 122],
  [6, 30, 54, 78, 102, 126],
  [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134],
  [6, 34, 60, 86, 112, 138],
  [6, 30, 58, 86, 114, 142],
  [6, 34, 62, 90, 118, 146],
  [6, 30, 54, 78, 102, 126, 150],
  [6, 24, 50, 76, 102, 128, 154],
  [6, 28, 54, 80, 106, 132, 158],
  [6, 32, 58, 84, 110, 136, 162],
  [6, 26, 54, 82, 110, 138, 166],
  [6, 30, 58, 86, 114, 142, 170],
];

const G15 =
  (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0);
const G18 =
  (1 << 12) |
  (1 << 11) |
  (1 << 10) |
  (1 << 9) |
  (1 << 8) |
  (1 << 5) |
  (1 << 2) |
  (1 << 0);
const G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1);

// Per version (1..40) and level (L, M, Q, H): [count, total, data, ...].
const RS_BLOCK_TABLE: number[][] = [
  // 1
  [1, 26, 19],
  [1, 26, 16],
  [1, 26, 13],
  [1, 26, 9],
  // 2
  [1, 44, 34],
  [1, 44, 28],
  [1, 44, 22],
  [1, 44, 16],
  // 3
  [1, 70, 55],
  [1, 70, 44],
  [2, 35, 17],
  [2, 35, 13],
  // 4
  [1, 100, 80],
  [2, 50, 32],
  [2, 50, 24],
  [4, 25, 9],
  // 5
  [1, 134, 108],
  [2, 67, 43],
  [2, 33, 15, 2, 34, 16],
  [2, 33, 11, 2, 34, 12],
  // 6
  [2, 86, 68],
  [4, 43, 27],
  [4, 43, 19],
  [4, 43, 15],
  // 7
  [2, 98, 78],
  [4, 49, 31],
  [2, 32, 14, 4, 33, 15],
  [4, 39, 13, 1, 40, 14],
  // 8
  [2, 121, 97],
  [2, 60, 38, 2, 61, 39],
  [4, 40, 18, 2, 41, 19],
  [4, 40, 14, 2, 41, 15],
  // 9
  [2, 146, 116],
  [3, 58, 36, 2, 59, 37],
  [4, 36, 16, 4, 37, 17],
  [4, 36, 12, 4, 37, 13],
  // 10
  [2, 86, 68, 2, 87, 69],
  [4, 69, 43, 1, 70, 44],
  [6, 43, 19, 2, 44, 20],
  [6, 43, 15, 2, 44, 16],
  // 11
  [4, 101, 81],
  [1, 80, 50, 4, 81, 51],
  [4, 50, 22, 4, 51, 23],
  [3, 36, 12, 8, 37, 13],
  // 12
  [2, 116, 92, 2, 117, 93],
  [6, 58, 36, 2, 59, 37],
  [4, 46, 20, 6, 47, 21],
  [7, 42, 14, 4, 43, 15],
  // 13
  [4, 133, 107],
  [8, 59, 37, 1, 60, 38],
  [8, 44, 20, 4, 45, 21],
  [12, 33, 11, 4, 34, 12],
  // 14
  [3, 145, 115, 1, 146, 116],
  [4, 64, 40, 5, 65, 41],
  [11, 36, 16, 5, 37, 17],
  [11, 36, 12, 5, 37, 13],
  // 15
  [5, 109, 87, 1, 110, 88],
  [5, 65, 41, 5, 66, 42],
  [5, 54, 24, 7, 55, 25],
  [11, 36, 12, 7, 37, 13],
  // 16
  [5, 122, 98, 1, 123, 99],
  [7, 73, 45, 3, 74, 46],
  [15, 43, 19, 2, 44, 20],
  [3, 45, 15, 13, 46, 16],
  // 17
  [1, 135, 107, 5, 136, 108],
  [10, 74, 46, 1, 75, 47],
  [1, 50, 22, 15, 51, 23],
  [2, 42, 14, 17, 43, 15],
  // 18
  [5, 150, 120, 1, 151, 121],
  [9, 69, 43, 4, 70, 44],
  [17, 50, 22, 1, 51, 23],
  [2, 42, 14, 19, 43, 15],
  // 19
  [3, 141, 113, 4, 142, 114],
  [3, 70, 44, 11, 71, 45],
  [17, 47, 21, 4, 48, 22],
  [9, 39, 13, 16, 40, 14],
  // 20
  [3, 135, 107, 5, 136, 108],
  [3, 67, 41, 13, 68, 42],
  [15, 54, 24, 5, 55, 25],
  [15, 43, 15, 10, 44, 16],
  // 21
  [4, 144, 116, 4, 145, 117],
  [17, 68, 42],
  [17, 50, 22, 6, 51, 23],
  [19, 46, 16, 6, 47, 17],
  // 22
  [2, 139, 111, 7, 140, 112],
  [17, 74, 46],
  [7, 54, 24, 16, 55, 25],
  [34, 37, 13],
  // 23
  [4, 151, 121, 5, 152, 122],
  [4, 75, 47, 14, 76, 48],
  [11, 54, 24, 14, 55, 25],
  [16, 45, 15, 14, 46, 16],
  // 24
  [6, 147, 117, 4, 148, 118],
  [6, 73, 45, 14, 74, 46],
  [11, 54, 24, 16, 55, 25],
  [30, 46, 16, 2, 47, 17],
  // 25
  [8, 132, 106, 4, 133, 107],
  [8, 75, 47, 13, 76, 48],
  [7, 54, 24, 22, 55, 25],
  [22, 45, 15, 13, 46, 16],
  // 26
  [10, 142, 114, 2, 143, 115],
  [19, 74, 46, 4, 75, 47],
  [28, 50, 22, 6, 51, 23],
  [33, 46, 16, 4, 47, 17],
  // 27
  [8, 152, 122, 4, 153, 123],
  [22, 73, 45, 3, 74, 46],
  [8, 53, 23, 26, 54, 24],
  [12, 45, 15, 28, 46, 16],
  // 28
  [3, 147, 117, 10, 148, 118],
  [3, 73, 45, 23, 74, 46],
  [4, 54, 24, 31, 55, 25],
  [11, 45, 15, 31, 46, 16],
  // 29
  [7, 146, 116, 7, 147, 117],
  [21, 73, 45, 7, 74, 46],
  [1, 53, 23, 37, 54, 24],
  [19, 45, 15, 26, 46, 16],
  // 30
  [5, 145, 115, 10, 146, 116],
  [19, 75, 47, 10, 76, 48],
  [15, 54, 24, 25, 55, 25],
  [23, 45, 15, 25, 46, 16],
  // 31
  [13, 145, 115, 3, 146, 116],
  [2, 74, 46, 29, 75, 47],
  [42, 54, 24, 1, 55, 25],
  [23, 45, 15, 28, 46, 16],
  // 32
  [17, 145, 115],
  [10, 74, 46, 23, 75, 47],
  [10, 54, 24, 35, 55, 25],
  [19, 45, 15, 35, 46, 16],
  // 33
  [17, 145, 115, 1, 146, 116],
  [14, 74, 46, 21, 75, 47],
  [29, 54, 24, 19, 55, 25],
  [11, 45, 15, 46, 46, 16],
  // 34
  [13, 145, 115, 6, 146, 116],
  [14, 74, 46, 23, 75, 47],
  [44, 54, 24, 7, 55, 25],
  [59, 46, 16, 1, 47, 17],
  // 35
  [12, 151, 121, 7, 152, 122],
  [12, 75, 47, 26, 76, 48],
  [39, 54, 24, 14, 55, 25],
  [22, 45, 15, 41, 46, 16],
  // 36
  [6, 151, 121, 14, 152, 122],
  [6, 75, 47, 34, 76, 48],
  [46, 54, 24, 10, 55, 25],
  [2, 45, 15, 64, 46, 16],
  // 37
  [17, 152, 122, 4, 153, 123],
  [29, 74, 46, 14, 75, 47],
  [49, 54, 24, 10, 55, 25],
  [24, 45, 15, 46, 46, 16],
  // 38
  [4, 152, 122, 18, 153, 123],
  [13, 74, 46, 32, 75, 47],
  [48, 54, 24, 14, 55, 25],
  [42, 45, 15, 32, 46, 16],
  // 39
  [20, 147, 117, 4, 148, 118],
  [40, 75, 47, 7, 76, 48],
  [43, 54, 24, 22, 55, 25],
  [10, 45, 15, 67, 46, 16],
  // 40
  [19, 148, 118, 6, 149, 119],
  [18, 75, 47, 31, 76, 48],
  [34, 54, 24, 34, 55, 25],
  [20, 45, 15, 61, 46, 16],
];

type RsBlock = { totalCount: number; dataCount: number };

const getRsBlocks = (typeNumber: number, level: number): RsBlock[] => {
  // The table is ordered L, M, Q, H; the level constants are L=1, M=0, Q=3, H=2.
  const row = RS_BLOCK_TABLE[(typeNumber - 1) * 4 + [1, 0, 3, 2].indexOf(level)];
  if (!row) throw new Error(`bad rs block @ typeNumber:${typeNumber}`);
  const list: RsBlock[] = [];
  for (let i = 0; i < row.length / 3; i += 1) {
    for (let j = 0; j < row[i * 3]; j += 1) {
      list.push({ totalCount: row[i * 3 + 1], dataCount: row[i * 3 + 2] });
    }
  }
  return list;
};

// ---- GF(256) math --------------------------------------------------------

const EXP_TABLE: number[] = new Array<number>(256);
const LOG_TABLE: number[] = new Array<number>(256);
for (let i = 0; i < 8; i += 1) EXP_TABLE[i] = 1 << i;
for (let i = 8; i < 256; i += 1) {
  EXP_TABLE[i] =
    EXP_TABLE[i - 4] ^ EXP_TABLE[i - 5] ^ EXP_TABLE[i - 6] ^ EXP_TABLE[i - 8];
}
for (let i = 0; i < 255; i += 1) LOG_TABLE[EXP_TABLE[i]] = i;

const glog = (n: number): number => {
  if (n < 1) throw new Error(`glog(${n})`);
  return LOG_TABLE[n];
};

const gexp = (n: number): number => {
  let value = n;
  while (value < 0) value += 255;
  while (value >= 256) value -= 255;
  return EXP_TABLE[value];
};

// ---- Polynomials ---------------------------------------------------------

type Polynomial = {
  getAt: (index: number) => number;
  getLength: () => number;
  multiply: (other: Polynomial) => Polynomial;
  mod: (other: Polynomial) => Polynomial;
};

const qrPolynomial = (num: number[], shift: number): Polynomial => {
  let offset = 0;
  while (offset < num.length && num[offset] === 0) offset += 1;
  const coefficients: number[] = new Array<number>(
    num.length - offset + shift,
  ).fill(0);
  for (let i = 0; i < num.length - offset; i += 1) {
    coefficients[i] = num[i + offset];
  }

  const self: Polynomial = {
    getAt: (index) => coefficients[index],
    getLength: () => coefficients.length,
    multiply: (other) => {
      const product: number[] = new Array<number>(
        self.getLength() + other.getLength() - 1,
      ).fill(0);
      for (let i = 0; i < self.getLength(); i += 1) {
        for (let j = 0; j < other.getLength(); j += 1) {
          product[i + j] ^= gexp(glog(self.getAt(i)) + glog(other.getAt(j)));
        }
      }
      return qrPolynomial(product, 0);
    },
    mod: (other) => {
      if (self.getLength() - other.getLength() < 0) return self;
      const ratio = glog(self.getAt(0)) - glog(other.getAt(0));
      const remainder: number[] = [];
      for (let i = 0; i < self.getLength(); i += 1) {
        remainder.push(self.getAt(i));
      }
      for (let i = 0; i < other.getLength(); i += 1) {
        remainder[i] ^= gexp(glog(other.getAt(i)) + ratio);
      }
      return qrPolynomial(remainder, 0).mod(other);
    },
  };
  return self;
};

const getErrorCorrectPolynomial = (errorCorrectLength: number): Polynomial => {
  let a = qrPolynomial([1], 0);
  for (let i = 0; i < errorCorrectLength; i += 1) {
    a = a.multiply(qrPolynomial([1, gexp(i)], 0));
  }
  return a;
};

// ---- Bit buffer ----------------------------------------------------------

type BitBuffer = {
  getBuffer: () => number[];
  getLengthInBits: () => number;
  put: (num: number, length: number) => void;
  putBit: (bit: boolean) => void;
};

const qrBitBuffer = (): BitBuffer => {
  const buffer: number[] = [];
  let length = 0;
  const self: BitBuffer = {
    getBuffer: () => buffer,
    getLengthInBits: () => length,
    put: (num, bits) => {
      for (let i = 0; i < bits; i += 1) {
        self.putBit(((num >>> (bits - i - 1)) & 1) === 1);
      }
    },
    putBit: (bit) => {
      const bufIndex = Math.floor(length / 8);
      if (buffer.length <= bufIndex) buffer.push(0);
      if (bit) buffer[bufIndex] |= 0x80 >>> length % 8;
      length += 1;
    },
  };
  return self;
};

// ---- Utilities -----------------------------------------------------------

const getBCHDigit = (value: number): number => {
  let data = value;
  let digit = 0;
  while (data !== 0) {
    digit += 1;
    data >>>= 1;
  }
  return digit;
};

const getBCHTypeInfo = (data: number): number => {
  let d = data << 10;
  while (getBCHDigit(d) - getBCHDigit(G15) >= 0) {
    d ^= G15 << (getBCHDigit(d) - getBCHDigit(G15));
  }
  return ((data << 10) | d) ^ G15_MASK;
};

const getBCHTypeNumber = (data: number): number => {
  let d = data << 12;
  while (getBCHDigit(d) - getBCHDigit(G18) >= 0) {
    d ^= G18 << (getBCHDigit(d) - getBCHDigit(G18));
  }
  return (data << 12) | d;
};

const getMaskFunction = (
  maskPattern: number,
): ((i: number, j: number) => boolean) => {
  switch (maskPattern) {
    case 0:
      return (i: number, j: number) => (i + j) % 2 === 0;
    case 1:
      return (i: number) => i % 2 === 0;
    case 2:
      return (_i: number, j: number) => j % 3 === 0;
    case 3:
      return (i: number, j: number) => (i + j) % 3 === 0;
    case 4:
      return (i: number, j: number) =>
        (Math.floor(i / 2) + Math.floor(j / 3)) % 2 === 0;
    case 5:
      return (i: number, j: number) => ((i * j) % 2) + ((i * j) % 3) === 0;
    case 6:
      return (i: number, j: number) =>
        (((i * j) % 2) + ((i * j) % 3)) % 2 === 0;
    case 7:
      return (i: number, j: number) =>
        (((i * j) % 3) + ((i + j) % 2)) % 2 === 0;
    default:
      throw new Error(`bad maskPattern:${maskPattern}`);
  }
};

/** Bits of the Byte-mode length field for a version (1..40). */
const getByteLengthInBits = (type: number): number => {
  if (type >= 1 && type < 10) return 8;
  if (type < 41) return 16;
  throw new Error(`type:${type}`);
};

/** The original penalty score, used to pick the best mask pattern. */
const getLostPoint = (qr: QrCode): number => {
  const moduleCount = qr.getModuleCount();
  let lostPoint = 0;

  // LEVEL1
  for (let row = 0; row < moduleCount; row += 1) {
    for (let col = 0; col < moduleCount; col += 1) {
      let sameCount = 0;
      const dark = qr.isDark(row, col);
      for (let r = -1; r <= 1; r += 1) {
        if (row + r < 0 || moduleCount <= row + r) continue;
        for (let c = -1; c <= 1; c += 1) {
          if (col + c < 0 || moduleCount <= col + c) continue;
          if (r === 0 && c === 0) continue;
          if (dark === qr.isDark(row + r, col + c)) sameCount += 1;
        }
      }
      if (sameCount > 5) lostPoint += 3 + sameCount - 5;
    }
  }

  // LEVEL2
  for (let row = 0; row < moduleCount - 1; row += 1) {
    for (let col = 0; col < moduleCount - 1; col += 1) {
      let count = 0;
      if (qr.isDark(row, col)) count += 1;
      if (qr.isDark(row + 1, col)) count += 1;
      if (qr.isDark(row, col + 1)) count += 1;
      if (qr.isDark(row + 1, col + 1)) count += 1;
      if (count === 0 || count === 4) lostPoint += 3;
    }
  }

  // LEVEL3
  for (let row = 0; row < moduleCount; row += 1) {
    for (let col = 0; col < moduleCount - 6; col += 1) {
      if (
        qr.isDark(row, col) &&
        !qr.isDark(row, col + 1) &&
        qr.isDark(row, col + 2) &&
        qr.isDark(row, col + 3) &&
        qr.isDark(row, col + 4) &&
        !qr.isDark(row, col + 5) &&
        qr.isDark(row, col + 6)
      ) {
        lostPoint += 40;
      }
    }
  }
  for (let col = 0; col < moduleCount; col += 1) {
    for (let row = 0; row < moduleCount - 6; row += 1) {
      if (
        qr.isDark(row, col) &&
        !qr.isDark(row + 1, col) &&
        qr.isDark(row + 2, col) &&
        qr.isDark(row + 3, col) &&
        qr.isDark(row + 4, col) &&
        !qr.isDark(row + 5, col) &&
        qr.isDark(row + 6, col)
      ) {
        lostPoint += 40;
      }
    }
  }

  // LEVEL4
  let darkCount = 0;
  for (let col = 0; col < moduleCount; col += 1) {
    for (let row = 0; row < moduleCount; row += 1) {
      if (qr.isDark(row, col)) darkCount += 1;
    }
  }
  const ratio =
    Math.abs((100 * darkCount) / moduleCount / moduleCount - 50) / 5;
  return lostPoint + ratio * 10;
};

/** UTF-8 bytes of a string (the original multibyte shim). */
const toUtf8Bytes = (value: string): number[] => {
  const utf8: number[] = [];
  for (let i = 0; i < value.length; i += 1) {
    let charcode = value.charCodeAt(i);
    if (charcode < 0x80) {
      utf8.push(charcode);
    } else if (charcode < 0x800) {
      utf8.push(0xc0 | (charcode >> 6), 0x80 | (charcode & 0x3f));
    } else if (charcode < 0xd800 || charcode >= 0xe000) {
      utf8.push(
        0xe0 | (charcode >> 12),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f),
      );
    } else {
      // surrogate pair
      i += 1;
      charcode =
        0x10000 + (((charcode & 0x3ff) << 10) | (value.charCodeAt(i) & 0x3ff));
      utf8.push(
        0xf0 | (charcode >> 18),
        0x80 | ((charcode >> 12) & 0x3f),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f),
      );
    }
  }
  return utf8;
};

// ---- The generator -------------------------------------------------------

export type QrCode = {
  /** Adds a UTF-8 string in Byte mode. */
  addData: (data: string) => void;
  /** True when the module at (row, col) is dark. */
  isDark: (row: number, col: number) => boolean;
  /** Modules per side. */
  getModuleCount: () => number;
  /** Builds the matrix; call after `addData`. */
  make: () => void;
};

/**
 * Creates a QR code generator.
 * @param typeNumber 1 to 40, or 0 to pick the smallest version that fits
 * @param errorCorrectionLevel 'L', 'M', 'Q' or 'H'
 */
export const createQrCode = (
  typeNumber: number,
  errorCorrectionLevel: QrErrorCorrectionLevel,
): QrCode => {
  const PAD0 = 0xec;
  const PAD1 = 0x11;

  let version = typeNumber;
  const level = ERROR_CORRECTION_LEVEL[errorCorrectionLevel];
  let modules: (boolean | null)[][] = [];
  let moduleCount = 0;
  let dataCache: number[] | null = null;
  const dataList: number[][] = [];

  const writeData = (buffer: BitBuffer, bytes: number[], type: number) => {
    buffer.put(MODE_8BIT_BYTE, 4);
    buffer.put(bytes.length, getByteLengthInBits(type));
    for (const byte of bytes) buffer.put(byte, 8);
  };

  const setupPositionProbePattern = (row: number, col: number) => {
    for (let r = -1; r <= 7; r += 1) {
      if (row + r <= -1 || moduleCount <= row + r) continue;
      for (let c = -1; c <= 7; c += 1) {
        if (col + c <= -1 || moduleCount <= col + c) continue;
        modules[row + r][col + c] =
          (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
          (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
          (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      }
    }
  };

  const setupTimingPattern = () => {
    for (let r = 8; r < moduleCount - 8; r += 1) {
      if (modules[r][6] != null) continue;
      modules[r][6] = r % 2 === 0;
    }
    for (let c = 8; c < moduleCount - 8; c += 1) {
      if (modules[6][c] != null) continue;
      modules[6][c] = c % 2 === 0;
    }
  };

  const setupPositionAdjustPattern = () => {
    const pos = PATTERN_POSITION_TABLE[version - 1];
    for (const row of pos) {
      for (const col of pos) {
        if (modules[row][col] != null) continue;
        for (let r = -2; r <= 2; r += 1) {
          for (let c = -2; c <= 2; c += 1) {
            modules[row + r][col + c] =
              r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0);
          }
        }
      }
    }
  };

  const setupTypeNumber = (test: boolean) => {
    const bits = getBCHTypeNumber(version);
    for (let i = 0; i < 18; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      modules[Math.floor(i / 3)][(i % 3) + moduleCount - 8 - 3] = mod;
      modules[(i % 3) + moduleCount - 8 - 3][Math.floor(i / 3)] = mod;
    }
  };

  const setupTypeInfo = (test: boolean, maskPattern: number) => {
    const bits = getBCHTypeInfo((level << 3) | maskPattern);

    // vertical
    for (let i = 0; i < 15; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      if (i < 6) {
        modules[i][8] = mod;
      } else if (i < 8) {
        modules[i + 1][8] = mod;
      } else {
        modules[moduleCount - 15 + i][8] = mod;
      }
    }

    // horizontal
    for (let i = 0; i < 15; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      if (i < 8) {
        modules[8][moduleCount - i - 1] = mod;
      } else if (i < 9) {
        modules[8][15 - i - 1 + 1] = mod;
      } else {
        modules[8][15 - i - 1] = mod;
      }
    }

    // fixed module
    modules[moduleCount - 8][8] = !test;
  };

  const mapData = (data: number[], maskPattern: number) => {
    let inc = -1;
    let row = moduleCount - 1;
    let bitIndex = 7;
    let byteIndex = 0;
    const maskFunc = getMaskFunction(maskPattern);

    for (let col = moduleCount - 1; col > 0; col -= 2) {
      if (col === 6) col -= 1;
      for (;;) {
        for (let c = 0; c < 2; c += 1) {
          if (modules[row][col - c] == null) {
            let dark = false;
            if (byteIndex < data.length) {
              dark = ((data[byteIndex] >>> bitIndex) & 1) === 1;
            }
            if (maskFunc(row, col - c)) dark = !dark;
            modules[row][col - c] = dark;
            bitIndex -= 1;
            if (bitIndex === -1) {
              byteIndex += 1;
              bitIndex = 7;
            }
          }
        }
        row += inc;
        if (row < 0 || moduleCount <= row) {
          row -= inc;
          inc = -inc;
          break;
        }
      }
    }
  };

  const createBytes = (buffer: BitBuffer, rsBlocks: RsBlock[]): number[] => {
    let offset = 0;
    let maxDcCount = 0;
    let maxEcCount = 0;
    const dcdata: number[][] = [];
    const ecdata: number[][] = [];

    rsBlocks.forEach((block, r) => {
      const dcCount = block.dataCount;
      const ecCount = block.totalCount - dcCount;
      maxDcCount = Math.max(maxDcCount, dcCount);
      maxEcCount = Math.max(maxEcCount, ecCount);

      dcdata[r] = [];
      for (let i = 0; i < dcCount; i += 1) {
        dcdata[r][i] = 0xff & buffer.getBuffer()[i + offset];
      }
      offset += dcCount;

      const rsPoly = getErrorCorrectPolynomial(ecCount);
      const rawPoly = qrPolynomial(dcdata[r], rsPoly.getLength() - 1);
      const modPoly = rawPoly.mod(rsPoly);
      ecdata[r] = [];
      const ecLength = rsPoly.getLength() - 1;
      for (let i = 0; i < ecLength; i += 1) {
        const modIndex = i + modPoly.getLength() - ecLength;
        ecdata[r][i] = modIndex >= 0 ? modPoly.getAt(modIndex) : 0;
      }
    });

    const data: number[] = [];
    for (let i = 0; i < maxDcCount; i += 1) {
      for (let r = 0; r < rsBlocks.length; r += 1) {
        if (i < dcdata[r].length) data.push(dcdata[r][i]);
      }
    }
    for (let i = 0; i < maxEcCount; i += 1) {
      for (let r = 0; r < rsBlocks.length; r += 1) {
        if (i < ecdata[r].length) data.push(ecdata[r][i]);
      }
    }
    return data;
  };

  const createData = (type: number): number[] => {
    const rsBlocks = getRsBlocks(type, level);
    const buffer = qrBitBuffer();
    for (const bytes of dataList) writeData(buffer, bytes, type);

    const totalDataCount = rsBlocks.reduce(
      (sum, block) => sum + block.dataCount,
      0,
    );
    if (buffer.getLengthInBits() > totalDataCount * 8) {
      throw new Error(
        `code length overflow. (${buffer.getLengthInBits()}>${totalDataCount * 8})`,
      );
    }
    // end code
    if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) buffer.put(0, 4);
    // padding
    while (buffer.getLengthInBits() % 8 !== 0) buffer.putBit(false);
    for (;;) {
      if (buffer.getLengthInBits() >= totalDataCount * 8) break;
      buffer.put(PAD0, 8);
      if (buffer.getLengthInBits() >= totalDataCount * 8) break;
      buffer.put(PAD1, 8);
    }
    return createBytes(buffer, rsBlocks);
  };

  const makeImpl = (test: boolean, maskPattern: number) => {
    moduleCount = version * 4 + 17;
    modules = Array.from({ length: moduleCount }, () =>
      new Array<boolean | null>(moduleCount).fill(null),
    );

    setupPositionProbePattern(0, 0);
    setupPositionProbePattern(moduleCount - 7, 0);
    setupPositionProbePattern(0, moduleCount - 7);
    setupPositionAdjustPattern();
    setupTimingPattern();
    setupTypeInfo(test, maskPattern);
    if (version >= 7) setupTypeNumber(test);
    if (dataCache == null) dataCache = createData(version);
    mapData(dataCache, maskPattern);
  };

  const self: QrCode = {
    addData: (data) => {
      dataList.push(toUtf8Bytes(data));
      dataCache = null;
    },
    isDark: (row, col) => {
      if (row < 0 || moduleCount <= row || col < 0 || moduleCount <= col) {
        throw new Error(`${row},${col}`);
      }
      return modules[row][col] === true;
    },
    getModuleCount: () => moduleCount,
    make: () => {
      if (version < 1) {
        let candidate = 1;
        for (; candidate < 40; candidate += 1) {
          const rsBlocks = getRsBlocks(candidate, level);
          const buffer = qrBitBuffer();
          for (const bytes of dataList) writeData(buffer, bytes, candidate);
          const totalDataCount = rsBlocks.reduce(
            (sum, block) => sum + block.dataCount,
            0,
          );
          if (buffer.getLengthInBits() <= totalDataCount * 8) break;
        }
        version = candidate;
      }

      let minLostPoint = 0;
      let pattern = 0;
      for (let i = 0; i < 8; i += 1) {
        makeImpl(true, i);
        const lostPoint = getLostPoint(self);
        if (i === 0 || minLostPoint > lostPoint) {
          minLostPoint = lostPoint;
          pattern = i;
        }
      }
      makeImpl(false, pattern);
    },
  };
  return self;
};

/**
 * Convenience wrapper: the dark-module matrix (`true` = dark) of a text at
 * error correction level M and the smallest fitting version.
 */
export const qrMatrix = (text: string): boolean[][] => {
  const qr = createQrCode(0, "M");
  qr.addData(text);
  qr.make();
  const size = qr.getModuleCount();
  return Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_unused, col) => qr.isDark(row, col)),
  );
};
